import axios from 'axios';
import { getTokens, removeTokens, saveTokens } from './tokenUtils';

// Create axios instance with default config
const axiosInstance = axios.create({
    baseURL: '/api', // Add back the /api prefix since we're using the proxy
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
// Store failed requests
let failedQueue = [];

const processQueue = (error = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve();
        }
    });
    failedQueue = [];
};

// Request interceptor
axiosInstance.interceptors.request.use(
    (config) => {
        // console.log('Making request to:', config.url);
        const tokens = getTokens();
        if (tokens && tokens.accessToken) {
            config.headers.Authorization = `Bearer ${tokens.accessToken}`;
        }
        return config;
    },
    (error) => {
        console.error('Request error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor
axiosInstance.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // If the error is 401 and we haven't tried to refresh the token yet
        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then(() => axiosInstance(originalRequest))
                    .catch(err => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const tokens = getTokens();
                if (!tokens || !tokens.refreshToken) {
                    throw new Error('No refresh token available');
                }

                const response = await axiosInstance.post('/refresh', { refresh_token: tokens.refreshToken });
                const { access_token, refresh_token } = response.data;

                // Update tokens
                saveTokens(access_token, refresh_token, tokens.userData);

                // Update the failed request's authorization header
                originalRequest.headers.Authorization = `Bearer ${access_token}`;

                processQueue();
                return axiosInstance(originalRequest);
            } catch (refreshError) {
                console.log('Refresh token error:', refreshError);
                processQueue(refreshError);
                removeTokens();
                // Redirect to login page
                window.location.href = '/login';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

export default axiosInstance; 